import React from 'react';
import { Avatar } from '@/components/ui/Avatar/Avatar';
import { Bot, BookOpen } from 'lucide-react';
import { RorosActorType } from '@/types/roros/enums';
import type { RorosActorDTO } from '@/types/roros/actors';
import { useAttachmentThumbnail } from './useAttachmentThumbnail';

interface ActorAvatarProps {
  actor: RorosActorDTO;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const SIZE_CLASSES = {
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-14 w-14',
};

const ICON_CLASSES = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-7 w-7',
};

export function ActorAvatar({ actor, size = 'md', className = '' }: ActorAvatarProps) {
  const sizeClass = SIZE_CLASSES[size];
  // Thumbnail hook — returns null when no imageAttachmentId or not yet loaded.
  // Human actors never have imageAttachmentId (schema enforces it), so this
  // is a no-op for them and costs nothing.
  const thumbnailUrl = useAttachmentThumbnail(actor.imageAttachmentId ?? null);

  if (actor.actorType === RorosActorType.HUMAN) {
    // Human actors carry the linked user's avatar via userAvatarUrl (LEFT
    // JOIN on users in the actor read queries). Fall through to the shared
    // Avatar component — it shows the initials placeholder when src is
    // missing or the image fails to load.
    return (
      <Avatar
        src={actor.userAvatarUrl ?? undefined}
        name={actor.userFullName ?? actor.name}
        size={size === 'lg' ? 'lg' : size === 'sm' ? 'sm' : 'md'}
        className={className}
      />
    );
  }

  // Non-human actor with an uploaded image — show the thumbnail.
  if (thumbnailUrl) {
    return (
      // D.3.4: role="img" marks the container as an image landmark for AT.
      // aria-label is already present; role makes the landmark explicit.
      <div
        role="img"
        aria-label={actor.name}
        className={`${sizeClass} rounded-full overflow-hidden flex-shrink-0 ${className}`}
      >
        <img
          src={thumbnailUrl}
          alt={actor.name}
          className="w-full h-full object-cover"
          data-testid="actor-avatar-thumbnail"
        />
      </div>
    );
  }

  // AI operator or knowledge persona — type icon fallback.
  const Icon = actor.actorType === RorosActorType.AI_OPERATOR ? Bot : BookOpen;
  const bgColor =
    actor.actorType === RorosActorType.AI_OPERATOR
      ? 'bg-purple-100 text-purple-600'
      : 'bg-amber-100 text-amber-600';

  return (
    // D.3.4: role="img" + aria-label for icon-only fallback branch.
    // Without these the div is an anonymous container — screen readers
    // cannot identify it as an image representing the actor.
    <div
      role="img"
      aria-label={actor.name}
      className={`${sizeClass} rounded-full ${bgColor} flex items-center justify-center flex-shrink-0 ${className}`}
    >
      <Icon className={ICON_CLASSES[size]} aria-hidden="true" />
    </div>
  );
}
